import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, ExternalLink, Pause, Play, PhoneCall, RefreshCw, Search } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { MessageModal } from "../../components/ui/MessageModal";
import { AgentStatusChip, AgentStatusDot } from "../../components/AgentStatusChip";
import { ErrorTile, Td, Th, fmtDateTime, fmtDuration } from "../../components/reports/ReportBits";
import { useLivePolling } from "../../hooks/useLivePolling";
import { useSession } from "../../hooks/useSession";
import { useOpenAsCustomer } from "../../hooks/useOpenAsCustomer";
import { customersAdminApi } from "../../api/customersAdminApi";
import { ScrollShadow } from "../../components/ui/ScrollShadow";
import { pbxExtensionsApi, type PbxCustomer, type PbxExtension, type PbxExtensionList, type RegistrationState } from "../../api/pbxExtensionsApi";
import { ExtensionBadge, OnCallChip, RegistrationChip, Pill, SegmentedFilter, fmtAgo, identityLabel, inputStyle, monoStyle, phoneModel } from "./pbxExtensionBits";

type StatusFilter = "all" | "registered" | "unregistered" | "never" | "oncall";
type SortKey = "customer" | "name" | "state" | "lastRegister" | "flaps" | "calls" | "lastCall";

const POLL_MS = 5000;
const STATE_ORDER: Record<RegistrationState, number> = { registered: 0, expired: 1, unregistered: 2, never: 3 };
const PAUSE_KEY = "pbx-extensions-paused";

function loadPaused(): boolean {
  try { return localStorage.getItem(PAUSE_KEY) === "1"; } catch { return false; }
}

/**
 * IPPBX → Extensions: every agent with a PBX login across customers, with
 * live SIP registration state (dbo.PbxEvent, polled every 5 s), phone, IP,
 * call stats and actions. Admins see all customers, managers their own.
 */
export function PbxExtensionsPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const [params, setParams] = useSearchParams();
  const isAdmin = session?.kind === "Admin";

  const customerId = params.get("customer") ? Number(params.get("customer")) : null;
  const status = (params.get("status") as StatusFilter) || "all";
  const search = params.get("q") ?? "";
  const sortKey = (params.get("sort") as SortKey) || "state";
  const sortDir = params.get("dir") === "desc" ? "desc" : "asc";

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const [paused, setPaused] = useState(loadPaused);
  useEffect(() => {
    try { localStorage.setItem(PAUSE_KEY, paused ? "1" : "0"); } catch { /* ignore */ }
  }, [paused]);

  // Only enabled customers with IPPBX enabled (in the caller's scope).
  const [customers, setCustomers] = useState<PbxCustomer[]>([]);
  useEffect(() => {
    let cancelled = false;
    pbxExtensionsApi.customers().then((r) => { if (!cancelled) setCustomers(r); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  // Search: type freely, the filter follows after a short pause.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => { setSearchInput(search); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchInput === search) return;
    const t = window.setTimeout(() => setParam("q", searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Times are UTC unless one customer is selected: then that customer's report zone.
  const [tz, setTz] = useState<string>("UTC");
  useEffect(() => {
    let cancelled = false;
    if (!customerId) { setTz("UTC"); return; }
    customersAdminApi.getTimeZone(customerId)
      .then((r) => { if (!cancelled) setTz(r.timeZoneIana || "UTC"); })
      .catch(() => { if (!cancelled) setTz("UTC"); });
    return () => { cancelled = true; };
  }, [customerId]);

  const { open: openAsCustomer, switching, error: switchError } = useOpenAsCustomer();

  const { data, loading, refreshing, error, refresh } = useLivePolling<PbxExtensionList>({
    fetcher: (signal) => pbxExtensionsApi.list(customerId, signal),
    intervalMs: POLL_MS,
    enabled: !!session?.isAuthenticated && !paused && switching == null,
    deps: [customerId],
  });

  const items = data?.items ?? [];
  const summary = data?.summary;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = items;
    if (status === "registered") rows = rows.filter((r) => r.registrationState === "registered");
    else if (status === "unregistered") rows = rows.filter((r) => r.registrationState === "unregistered" || r.registrationState === "expired");
    else if (status === "never") rows = rows.filter((r) => r.registrationState === "never");
    else if (status === "oncall") rows = rows.filter((r) => r.activeCalls > 0);
    if (q) {
      rows = rows.filter((r) =>
        [r.agentName, r.agentRef, r.pbxExtension, r.pbxUsername, r.customerName, r.managerName, r.ipAddress, r.userAgent, r.pbxHostname]
          .some((v) => (v ?? "").toLowerCase().includes(q)),
      );
    }
    const dir = sortDir === "desc" ? -1 : 1;
    const cmpStr = (a: string | null, b: string | null) => (a ?? "").localeCompare(b ?? "");
    const cmpNum = (a: number | null, b: number | null) => (a ?? -1) - (b ?? -1);
    const cmpDate = (a: string | null, b: string | null) => (a ? Date.parse(a) : 0) - (b ? Date.parse(b) : 0);
    return rows.slice().sort((a, b) => {
      let c = 0;
      switch (sortKey) {
        case "customer": c = cmpStr(a.customerName, b.customerName) || cmpStr(a.agentName, b.agentName); break;
        case "name": c = cmpStr(a.agentName, b.agentName); break;
        case "state": c = STATE_ORDER[a.registrationState] - STATE_ORDER[b.registrationState] || cmpStr(a.customerName, b.customerName) || cmpStr(a.agentName, b.agentName); break;
        case "lastRegister": c = cmpDate(a.lastRegisterUtc, b.lastRegisterUtc); break;
        case "flaps": c = cmpNum(a.unregisters24h, b.unregisters24h); break;
        case "calls": c = cmpNum(a.callsTodayIn + (a.callsTodayOut ?? 0), b.callsTodayIn + (b.callsTodayOut ?? 0)); break;
        case "lastCall": c = cmpDate(a.lastCallUtc, b.lastCallUtc); break;
      }
      return c * dir;
    });
  }, [items, status, search, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setParam("dir", sortDir === "asc" ? "desc" : "asc");
    else {
      const next = new URLSearchParams(params);
      next.set("sort", key);
      next.set("dir", key === "flaps" || key === "calls" || key === "lastRegister" || key === "lastCall" ? "desc" : "asc");
      setParams(next, { replace: true });
    }
  };

  // ---- actions ------------------------------------------------------------------------
  const [confirmRow, setConfirmRow] = useState<PbxExtension | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ title: string; body: string } | null>(null);

  const doReregister = async () => {
    if (!confirmRow) return;
    setBusy(true);
    try {
      const r = await pbxExtensionsApi.reregister(confirmRow.agentId);
      setMessage({
        title: r.alreadyPending ? "Re-registration already pending" : "Re-registration requested",
        body: `${confirmRow.agentName} (${r.pbxUsername}) will be flushed from the PBX within a few seconds; the phone registers again at its next interval, usually within 1–3 minutes.`,
      });
      void refresh();
    } catch (e) {
      setMessage({ title: "Re-registration failed", body: e instanceof Error ? e.message : "Unknown error" });
    } finally {
      setBusy(false);
      setConfirmRow(null);
    }
  };

  const openDestination = (row: PbxExtension) => {
    if (!row.customerEnabled) return;
    void openAsCustomer(row.customerId, `/destinations/${row.agentId}`);
  };

  const gotoDetail = (row: PbxExtension) => navigate(`/ippbx/extensions/${row.agentId}`);
  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement | HTMLDivElement>, row: PbxExtension) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); gotoDetail(row); }
  };

  // The right scroll fade sits just left of the pinned actions column, so measure that column.
  // The fade is positioned from the wrapper's right edge, which includes the list's vertical
  // scrollbar; the pinned cells live inside the scroller, so add the scrollbar width.
  const actionsHeadRef = useRef<HTMLTableCellElement | null>(null);
  const [actionsWidth, setActionsWidth] = useState(96);
  useEffect(() => {
    const el = actionsHeadRef.current;
    if (!el) return;
    const measure = () => {
      const scroller = el.closest(".scroll-shadow-inner") as HTMLElement | null;
      const scrollbar = scroller ? scroller.offsetWidth - scroller.clientWidth : 0;
      setActionsWidth(Math.ceil(el.getBoundingClientRect().width) + Math.max(0, scrollbar));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const scroller = el.closest(".scroll-shadow-inner");
    if (scroller) ro.observe(scroller);
    measure();
    return () => ro.disconnect();
  }, [filtered.length]);

  const updatedLabel = data?.generatedUtc ? fmtDateTime(data.generatedUtc, tz).slice(-8) : null;
  const statusOptions: { value: StatusFilter; label: string; count?: number }[] = [
    { value: "all", label: "All", count: summary?.total },
    { value: "registered", label: "Registered", count: summary?.registered },
    { value: "unregistered", label: "Unregistered", count: summary?.unregistered },
    { value: "never", label: "Never", count: summary?.never },
    { value: "oncall", label: "On call", count: summary?.onCall },
  ];

  const sortIcon = (key: SortKey) => (sortKey === key ? (sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : null);
  const sortableTh = (key: SortKey, label: string, align?: "right") => (
    <Th align={align}>
      <button type="button" onClick={() => toggleSort(key)} style={{ all: "unset", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label} {sortIcon(key)}
      </button>
    </Th>
  );

  return (
    <>
      <PageHeader
        title="IPPBX extensions"
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.8rem", color: "var(--portal-muted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: paused ? "var(--portal-border)" : refreshing ? "var(--portal-warn)" : "var(--portal-status-success)" }} />
              {paused ? "Paused" : updatedLabel ? `Updated ${updatedLabel}` : "Live"}
            </span>
            <button type="button" className="btn btn-secondary" onClick={() => setPaused((p) => !p)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {paused ? <Play size={14} /> : <Pause size={14} />} {paused ? "Resume" : "Pause"}
            </button>
          </div>
        }
      />
      <div className="dashboard" style={{ display: "grid", gap: 16 }}>
        <p style={{ margin: 0, color: "var(--portal-muted)", fontSize: "0.9rem" }}>
          Every destination with a PBX login and what its phone is doing right now: registration (from the PBX's register/unregister
          events), phone model, IP address, calls and flaps. Click a row for the full history.
        </p>

        {summary && (
          <div className="tile-cards" style={{ ["--tile-min" as string]: "150px" }}>
            {statusOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                className="tile"
                onClick={() => setParam("status", o.value === "all" ? null : o.value)}
                aria-pressed={status === o.value}
                style={{ cursor: "pointer", textAlign: "left", padding: "14px 18px", gap: 4, borderColor: status === o.value ? "var(--portal-primary)" : undefined, fontFamily: "inherit" }}
              >
                <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--portal-muted)" }}>{o.label === "Never" ? "Never registered" : o.label}</div>
                <div style={{ fontSize: "1.6rem", fontWeight: 700, lineHeight: 1.1 }}>{o.count ?? 0}</div>
              </button>
            ))}
          </div>
        )}

        <section className="tile" style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <select
              className="portal-input-inline"
              style={{ ...inputStyle, maxWidth: 340, flex: "1 1 220px" }}
              value={customerId ?? ""}
              onChange={(e) => setParam("customer", e.target.value || null)}
              aria-label="Customer"
            >
              <option value="">{isAdmin ? "All customers" : "All my customers"}</option>
              {customers.map((c) => (
                <option key={c.customerId} value={c.customerId}>{c.customerName}{isAdmin ? ` · ${c.managerName}` : ""} ({c.extensions})</option>
              ))}
            </select>
            <SegmentedFilter value={status} options={statusOptions} onChange={(v) => setParam("status", v === "all" ? null : v)} ariaLabel="Registration status" />
            <label style={{ position: "relative", flex: "1 1 220px", maxWidth: 360 }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--portal-muted)" }} />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search agent, ref, username, customer, IP, phone…"
                style={{ ...inputStyle, width: "100%", paddingLeft: 30 }}
                aria-label="Search"
              />
            </label>
            <span style={{ marginLeft: "auto", fontSize: "0.8rem", color: "var(--portal-muted)" }}>
              {filtered.length} of {items.length} · times in {tz}
            </span>
          </div>

          {switchError && <div className="alert alert-error">{switchError}</div>}
          {error != null && <ErrorTile message="The extension list could not be refreshed. Showing the last known data." />}

          {loading && !data ? (
            <div style={{ color: "var(--portal-muted)" }}>Loading extensions…</div>
          ) : items.length === 0 ? (
            <div style={{ color: "var(--portal-muted)" }}>
              {isAdmin ? "No PBX extensions found." : "None of your customers has an IPPBX extension yet."}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ color: "var(--portal-muted)" }}>No extensions match the current filter.</div>
          ) : (
            <>
              <div className="pbx-ext-table" style={{ ["--pbx-actions-w" as string]: `${actionsWidth}px` }}>
                <ScrollShadow>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                  <thead>
                    <tr>
                      {sortableTh("state", "Registration")}
                      {sortableTh("name", "Extension")}
                      {sortableTh("customer", "Customer")}
                      <Th>Status</Th>
                      <Th>Phone</Th>
                      {sortableTh("flaps", "Flaps 24 h", "right")}
                      {sortableTh("calls", "Calls today (UTC)", "right")}
                      <Th>Identity</Th>
                      <th ref={actionsHeadRef} style={{ textAlign: "right", padding: "10px 14px", borderBottom: "1px solid var(--portal-border)", color: "var(--portal-muted)", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr
                        key={r.agentId}
                        onClick={() => gotoDetail(r)}
                        onKeyDown={(e) => onRowKey(e, r)}
                        tabIndex={0}
                        role="link"
                        style={{ cursor: "pointer" }}
                        className="pbx-ext-row"
                      >
                        <Td>
                          <span title={r.lastRegisterUtc ? `${r.activeCalls > 0 ? "Registered, on a call. " : ""}Last register ${fmtAgo(r.secondsSinceRegister)} (${fmtDateTime(r.lastRegisterUtc, tz)})${r.ipAddress ? ` from ${r.ipAddress}` : ""}` : "No registration seen"}>
                            {r.activeCalls > 0 ? <OnCallChip count={r.activeCalls} size="sm" /> : <RegistrationChip state={r.registrationState} size="sm" />}
                          </span>
                        </Td>
                        <Td>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <ExtensionBadge extension={r.pbxExtension} size="sm" />
                            <span style={{ fontWeight: 600 }} title={r.pbxUsername ? `${r.agentRef} · ${r.pbxUsername}` : r.agentRef}>{r.agentName}</span>
                          </span>
                        </Td>
                        <Td>
                          <span title={isAdmin ? r.managerName : undefined}>{r.customerName}{r.customerEnabled ? "" : <span style={{ color: "var(--portal-muted)" }}> (disabled)</span>}</span>
                        </Td>
                        <Td>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <AgentStatusDot statusId={r.statusId} statusName={r.statusName} />
                            {!r.phoneEnabled && <Pill tone="muted">phone off</Pill>}
                          </div>
                        </Td>
                        <Td>
                          <span title={r.userAgent ?? undefined}>{phoneModel(r.userAgent)}</span>
                        </Td>
                        <Td align="right">
                          <span style={{ fontWeight: r.unregisters24h >= 3 ? 700 : 400, color: r.unregisters24h >= 3 ? "#a12a1f" : undefined }}>{r.unregisters24h}</span>
                        </Td>
                        <Td align="right">
                          <span title={r.talkSecondsToday > 0 ? `Talk time ${fmtDuration(r.talkSecondsToday)}` : undefined}>{r.callsTodayIn} in · {r.callsTodayOut ?? "n/a"} out</span>
                        </Td>
                        <Td>{identityLabel(r.pbxIdentityMode, r.pbxIdentityNumber)}</Td>
                        <Td align="right">
                          <div style={{ display: "inline-flex", gap: 6 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                            <button type="button" className="btn btn-secondary" title={r.pbxUsername ? "Force re-register" : "No PBX username"} disabled={!r.pbxUsername} onClick={() => setConfirmRow(r)} style={{ padding: "4px 8px" }} aria-label="Force re-register">
                              <RefreshCw size={14} />
                            </button>
                            <button type="button" className="btn btn-secondary" title={r.customerEnabled ? "Open destination (as customer)" : "This customer is disabled and cannot be impersonated."} disabled={!r.customerEnabled || switching != null} onClick={() => openDestination(r)} style={{ padding: "4px 8px" }} aria-label="Open destination">
                              <ExternalLink size={14} />
                            </button>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </ScrollShadow>
              </div>

              <div className="pbx-ext-cards tile-rows">
                {filtered.map((r) => (
                  <div key={r.agentId} className="tile" role="link" tabIndex={0} onClick={() => gotoDetail(r)} onKeyDown={(e) => onRowKey(e, r)} style={{ cursor: "pointer", gap: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><ExtensionBadge extension={r.pbxExtension} size="sm" />{r.agentName}</div>
                        <div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>{r.customerName} · {r.agentRef}</div>
                      </div>
                      <div style={{ display: "inline-flex", gap: 6 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                        <button type="button" className="btn btn-secondary" disabled={!r.pbxUsername} onClick={() => setConfirmRow(r)} style={{ padding: "4px 8px" }} aria-label="Force re-register"><RefreshCw size={14} /></button>
                        <button type="button" className="btn btn-secondary" disabled={!r.customerEnabled || switching != null} onClick={() => openDestination(r)} style={{ padding: "4px 8px" }} aria-label="Open destination"><ExternalLink size={14} /></button>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {r.activeCalls > 0 ? <OnCallChip count={r.activeCalls} size="sm" /> : <RegistrationChip state={r.registrationState} size="sm" />}
                      <AgentStatusChip statusId={r.statusId} statusName={r.statusName} size="sm" />
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "var(--portal-muted)", display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <span><PhoneCall size={12} style={{ verticalAlign: "-2px" }} /> {phoneModel(r.userAgent)}</span>
                      <span style={monoStyle}>{r.ipAddress ?? "—"}</span>
                      <span>{r.lastRegisterUtc ? fmtAgo(r.secondsSinceRegister) : "never registered"}</span>
                      <span>{r.callsTodayIn} in · {r.callsTodayOut ?? "n/a"} out today</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      <ConfirmModal
        open={confirmRow != null}
        onClose={() => (busy ? undefined : setConfirmRow(null))}
        title="Force re-register"
        confirmLabel="Re-register"
        tone="caution"
        busy={busy}
        onConfirm={doReregister}
      >
        {confirmRow && (
          <p style={{ margin: 0 }}>
            Flush the registration of <strong>{confirmRow.agentName}</strong> ({confirmRow.pbxUsername}){confirmRow.pbxHostname ? ` on ${confirmRow.pbxHostname}` : ""}?
            The phone registers again at its next interval, usually within 1–3 minutes. Active calls are not affected.
          </p>
        )}
      </ConfirmModal>
      <MessageModal open={message != null} onClose={() => setMessage(null)} title={message?.title ?? ""}>
        <p style={{ margin: 0 }}>{message?.body}</p>
      </MessageModal>
    </>
  );
}
